import { handleError } from './error/handle';

type Downloadable = {
  url: string;
  type?: string;
};

type DownloadableWithUrls = {
  urls: string[];
  type?: string;
};

type DownloadableData = Downloadable | DownloadableWithUrls;

/**
 * 下载单张图片
 */
const downloadSingle = (
  url: string,
  id: string,
  index: number,
  total: number,
  extension: string,
) => {
  return new Promise<void>((resolve, reject) => {
    const link = document.createElement('a');
    
    const filename = total > 1 
      ? `flowmuse_${id}_${index + 1}.${extension}`
      : `flowmuse_${id}.${extension}`;

    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * 获取文件扩展名
 */
const getExtension = (url: string, type?: string, defaultExtension: string = 'png'): string => {
  // 优先从 URL 中提取扩展名
  let extension: string | undefined;
  if (url) {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const match = pathname.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      if (match) {
        extension = match[1];
      }
    } catch {
      // URL 解析失败，忽略
    }
  }
  
  // 如果 URL 中没有扩展名，从 type 提取
  if (!extension && type) {
    extension = type.split('/').at(-1);
  }
  
  // 如果还是没有，使用默认扩展名
  extension = extension ?? defaultExtension;
  
  // 处理 jpeg -> jpg 的转换
  if (extension === 'jpeg') {
    extension = 'jpg';
  }
  
  return extension;
};

export const download = async (
  data: DownloadableData | undefined,
  id: string,
  defaultExtension: string,
  index?: number, // 可选：当前图片的索引（从0开始）
  total?: number, // 可选：总图片数
): Promise<void> => {
  if (!data) {
    handleError('Error downloading file', 'No data to download.');
    return;
  }

  // 检查是否有多张图片（urls 数组）
  const hasMultipleUrls = 'urls' in data && Array.isArray(data.urls) && data.urls.length > 1;
  const urls = hasMultipleUrls ? data.urls : ['url' in data ? data.url : ''];
  const type = data.type;

  if (urls.length === 0 || !urls[0]) {
    handleError('Error downloading file', 'No URL to download.');
    return;
  }

  // 单张图片：直接下载
  if (urls.length === 1) {
    const extension = getExtension(urls[0], type, defaultExtension);
    // 如果提供了 total 且大于1，即使只有一张图片也添加编号
    const actualTotal = total !== undefined && total > 1 ? total : 1;
    const actualIndex = index !== undefined ? index : 0;
    try {
      await downloadSingle(urls[0], id, actualIndex, actualTotal, extension);
    } catch (error: any) {
      handleError('Error downloading file', error?.message || 'Download failed');
      // 兜底：弹出原始链接
      try {
        if (typeof window !== 'undefined' && urls[0]) {
          const opened = window.open(urls[0], '_blank', 'noopener,noreferrer');
          if (!opened) {
            // eslint-disable-next-line no-alert
            window.prompt(
              'Download failed. Please copy this link to download manually:',
              urls[0],
            );
          }
        }
      } catch {
        // 忽略兜底中的任何异常
      }
    }
    return;
  }

  // 多张图片：依次下载（每个文件按自己的地址推断扩展名：各张格式可能不同，如部分缩略图转换失败回退为原格式）
  let completed = 0;
  let failed = 0;

  await Promise.all(
    urls.map((url, index) =>
      downloadSingle(url, id, index, urls.length, getExtension(url, type, defaultExtension))
        .then(() => {
          completed++;
        })
        .catch((error) => {
          failed++;
          console.error(`Failed to download image ${index + 1}:`, error);
        }),
    ),
  );

  if (failed > 0) {
    handleError(
      'Download completed with errors',
      `${completed} images downloaded successfully, ${failed} failed.`,
    );
  }
};

/**
 * 检查数据是否包含多张图片
 */
export const hasMultipleImages = (data: DownloadableData | undefined): boolean => {
  if (!data) return false;
  return 'urls' in data && Array.isArray(data.urls) && data.urls.length > 1;
};

/**
 * 获取图片数量
 */
export const getImageCount = (data: DownloadableData | undefined): number => {
  if (!data) return 0;
  if ('urls' in data && Array.isArray(data.urls)) {
    return data.urls.length;
  }
  return 1;
};
